import { Box, Typography } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { CategoryLabel } from 'entities/Category';
import {
    getTermFromCode,
    shouldShowUOM,
} from 'shared/lib/unitOfMeasureUtility/unitOfMeasureUtility';
import { decodeHtmlEntities } from 'shared/utils/textFormatters/textFormatters';

import { CatalogItemRow } from './CatalogItemRow';

export interface CatalogItemProps {
    sku: string;
    uom: string;
    qtyPerUom: number;
    minAllowedInStock: number;
    title: string;
    categoryId: string;
    label?: string;
}

export const CatalogItem = memo(
    ({
        sku,
        uom,
        qtyPerUom,
        minAllowedInStock,
        title,
        categoryId,
    }: CatalogItemProps) => {
        const { t } = useTranslation();
        const minAllowedInStockValue = minAllowedInStock || 0;

        return (
            <Box display="flex">
                <Box
                    flex="1"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '99px',
                    }}
                >
                    <Typography
                        data-testclass="item-title"
                        typography={'openSans.subtitle1Medium'}
                        mb="12px"
                    >
                        {decodeHtmlEntities(title)}
                    </Typography>

                    {categoryId && (
                        <Box
                            data-testclass="item-category-label"
                            mb="10px"
                            margin="-2px 0 10px"
                        >
                            <CategoryLabel categoryId={categoryId} />
                        </Box>
                    )}
                    <Box
                        width="250px"
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-evenly"
                        flex="1"
                        gap="4px"
                        paddingBottom="12px"
                    >
                        <CatalogItemRow label="SKU" value={sku} />
                        {shouldShowUOM(uom) && (
                            <>
                                <CatalogItemRow
                                    label="UOM"
                                    value={getTermFromCode(uom)}
                                />
                                {qtyPerUom > 1 && (
                                    <CatalogItemRow
                                        label={t('Qty per UOM')}
                                        value={qtyPerUom}
                                    />
                                )}
                            </>
                        )}
                        <CatalogItemRow
                            label="Min On Hand"
                            value={minAllowedInStockValue}
                        />
                    </Box>
                </Box>
            </Box>
        );
    },
);
